import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component,
    ElementRef, HostListener, OnDestroy, OnInit
} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {SkewSentimentService} from './skew-sentiment.service';
import {SessionService} from '../authentication/session-service.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {
    ExpirationResultDto,
    GetSkewSentimentDebugReply
} from './skew-sentiment-cqrs.model';
import {DetectMethodChanges, isValidNumber, makeGuiFriendlyExpirationDate} from '../utils';
import {DateTime} from 'luxon';

@Component({
    selector: 'ets-skew-sentiment-indicator',
    templateUrl: './skew-sentiment-indicator.component.html',
    styleUrls: ['./skew-sentiment-indicator.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkewSentimentIndicatorComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _skewSentimentService: SkewSentimentService,
        private readonly _sessionService: SessionService,
        private readonly _shellClient: ShellClientService,
        private readonly _elementRef: ElementRef
    ) {
    }

    private _unsubscriber = new Subject<void>();

    overlayVisible = false;
    overlayStyle: { [key: string]: string } = {};
    skewErrorTooltipVisible = false;

    // ── Debug Panel ──
    debugPanelVisible = false;
    debugLoading = false;

    get debugData(): GetSkewSentimentDebugReply | null {
        return this._skewSentimentService.debug;
    }

    readonly skewSentimentRanges = [
        {min: 91, max: Infinity, label: 'OVER $90', meaning: 'Greatly oversold, very high likelihood of the market going up.', color: 'blue'},
        {min: 75, max: 90, label: 'OVER $75', meaning: 'Getting oversold, high likelihood of the market going up.', color: 'blue'},
        {min: 41, max: 74, label: 'Between $41 and $74', meaning: 'Things are normal.', color: 'neutral'},
        {min: 31, max: 40, label: 'Under $40', meaning: 'The market is overbought.', color: 'red'},
        {min: 26, max: 30, label: 'Under $30', meaning: 'There is a high probability of the market dropping within a week.', color: 'red'},
        {min: 21, max: 25, label: 'Under $25', meaning: 'We should see a >2% decline within a week. (>68% chance).', color: 'red'},
        {min: -Infinity, max: 20, label: 'Under $20', meaning: 'We should see a >4% decline within a week. (>68% chance)', color: 'red'},
    ];

    // ── Getters ──

    get skewSentimentAvailable(): boolean {
        return isValidNumber(this._skewSentimentService.value, true);
    }

    get skewSentiment(): number {
        return this._skewSentimentService.value;
    }

    get skewLoading(): boolean {
        return this._skewSentimentService.timestamp == null;
    }

    get skewErrorMessage(): string {
        return this._skewSentimentService.errorMessage;
    }

    get isStale(): boolean {
        return false;
    }

    get isSuperUser(): boolean {
        return this._sessionService.isSuperUser;
    }

    // ── Lifecycle ──

    ngOnInit(): void {
        this._skewSentimentService.init();

        this._skewSentimentService.updated$
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => {
                this._changeDetector.detectChanges();
            });
    }

    ngOnDestroy(): void {
        this._unsubscriber.next();
        this._unsubscriber.complete();
        this._skewSentimentService.dispose();
    }

    // ── Overlay ──

    @DetectMethodChanges()
    toggleOverlay(): void {
        this.overlayVisible = !this.overlayVisible;
        if (this.overlayVisible) {
            const ssBox = this._elementRef.nativeElement.querySelector('.ss-box');
            if (ssBox) {
                const rect = ssBox.getBoundingClientRect();
                this.overlayStyle = {
                    left: (rect.left - 150) + 'px',
                    bottom: (window.innerHeight - rect.top + 4) + 'px'
                };
            }
        } else {
            this.debugPanelVisible = false;
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (!this.overlayVisible) { return; }
        if (this._elementRef.nativeElement.contains(event.target)) { return; }
        this.overlayVisible = false;
        this.debugPanelVisible = false;
        this._changeDetector.detectChanges();
    }

    // ── Display Logic ──

    getSkewSentimentColorClass(): string {
        const v = this.skewSentiment;
        if (v == null) { return 'skew-na'; }
        if (v >= 75) { return 'skew-blue'; }
        if (v >= 41) { return 'skew-neutral'; }
        return 'skew-red';
    }

    isSkewRangeActive(range: { min: number, max: number }): boolean {
        const v = this.skewSentiment;
        if (v == null) { return false; }
        return v >= range.min && v <= range.max;
    }

    @DetectMethodChanges()
    showErrorTooltip(event: MouseEvent): void {
        this.skewErrorTooltipVisible = true;
    }

    @DetectMethodChanges()
    hideErrorTooltip(): void {
        this.skewErrorTooltipVisible = false;
    }

    // ── Debug Panel ──

    @DetectMethodChanges({isAsync: true})
    async toggleDebugPanel(): Promise<void> {
        if (this.debugPanelVisible) {
            this.debugPanelVisible = false;
            return;
        }
        this.debugPanelVisible = true;
        // Debug payload normally arrives via the same push as the indicator value.
        // Backfill only if the panel is opened before the first push lands.
        if (this.debugData == null) {
            this.debugLoading = true;
            try {
                await this._skewSentimentService.backfillDebugIfMissing();
            } finally {
                this.debugLoading = false;
            }
        }
    }

    isOutlier(exp: ExpirationResultDto): boolean {
        if (!this.debugData || !this.debugData.excludedOutliers) { return false; }
        return this.debugData.excludedOutliers.indexOf(exp.expirationDate) >= 0;
    }

    isChosenExpiration(exp: ExpirationResultDto): boolean {
        if (!this.debugData || !this.debugData.chosenExpiration) { return false; }
        return this.debugData.chosenExpiration === exp.expirationDate;
    }

    formatExpDate(isoDate: string, dte?: number): string {
        if (!isoDate) { return ''; }
        const friendly = makeGuiFriendlyExpirationDate(isoDate);
        const dt = DateTime.fromFormat(isoDate, 'yyyy-MM-dd');
        const dow = dt.isValid ? dt.toFormat('EEE') : '';
        if (dte != null) {
            return `${dow} ${friendly} (${dte}d)`;
        }
        return `${dow} ${friendly}`;
    }

    formatOutlierDates(dates: string[]): string {
        if (!dates || dates.length === 0) { return ''; }
        // Find matching DTE from debug data expirations
        return dates.map(d => {
            const match = this.debugData?.expirations?.find(e => e.expirationDate === d);
            return this.formatExpDate(d, match?.dte);
        }).join(', ');
    }

}
